// DoctorExpForm.tsx
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import {
  PagerTargetEvent,
  HeaderThElement,
} from "@progress/kendo-react-data-tools";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import IconButton from "../../components/common/IconButton";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import ShadowCard from "../../components/common/ShadowCard";
import AlertBox from "../../components/common/AlertBox";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import { MdDelete, MdEdit } from "react-icons/md";
import {
  deleteDoctorExpDetail,
  getDoctorExpByDoctorID,
  insertDoctorExpDetail,
  updateDoctorExpDetail,
} from "./services/doctor.service";
import { clearDoctorExpDetails } from "./doctorSlice";
import FormDatePicker from "../../components/formFields/FormDateField";
import moment from "moment";
import FormSelectionField from "../../components/formFields/FormSelectionField";

interface PageState {
  skip: number;
  take: number;
}
const initialDataState: PageState = { skip: 0, take: 10 };

const DoctorExpForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const DoctorID = location.state?.DoctorID;

  const [editItem, setEditItem] = useState<any>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [formKey, setFormKey] = useState(1);
  const [page, setPage] = useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = useState<
    number | string | undefined
  >();

  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const expDetails = useAppSelector((state) => state.doctor.DoctorExpDetails);
  const loading = useAppSelector((state) => state.doctor.loading);
  const formLoading = useAppSelector((state) => state.doctor.formLoading);
  const Specialty = useAppSelector((state) => state.specialty?.SpecialtyList);

  useEffect(() => {
    setFormKey((p) => p + 1);
  }, [expDetails, editItem]);

  useEffect(() => {
    if (DoctorID) dispatch(getDoctorExpByDoctorID(DoctorID));
    return () => {
      dispatch(clearDoctorExpDetails());
    };
  }, [DoctorID]);

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? expDetails.length : event.page.take;
    if (targetEvent.value) setPageSizeValue(targetEvent.value);
    setPage({ ...event.page, take });
  };

  const handleAdd = async (values: any) => {
    const response = await dispatch(
      insertDoctorExpDetail({
        DoctorID,
        HospitalName: values.HospitalName,
        Address: values.Address,
        City: values.City,
        SpecialityID: values.SpecialityID || 0,
        FromMonthYear: values.FromMonthYear
          ? moment(values.FromMonthYear).format("YYYY-MM-DD")
          : "",
        ToMonthYear: values.ToMonthYear
          ? moment(values.ToMonthYear).format("YYYY-MM-DD")
          : "",
      }),
    );
    if (response?.meta?.requestStatus === "fulfilled") {
      dispatch(getDoctorExpByDoctorID(DoctorID));
      setEditItem(null);
    }
  };

  const handleUpdate = async (values: any) => {
    const response = await dispatch(
      updateDoctorExpDetail({
        ID: editItem.ID,
        DoctorID,
        HospitalName: values.HospitalName,
        Address: values.Address,
        City: values.City,
        SpecialityID: values.SpecialityID || null,
        FromMonthYear: values.FromMonthYear
          ? moment(values.FromMonthYear).format("YYYY-MM-DD")
          : "",
        ToMonthYear: values.ToMonthYear
          ? moment(values.ToMonthYear).format("YYYY-MM-DD")
          : "",
      }),
    );
    if (response?.meta?.requestStatus === "fulfilled") {
      dispatch(getDoctorExpByDoctorID(DoctorID));
      setEditItem(null);
    }
  };

  const handleDelete = async () => {
    if (deleteId) {
      const response = await dispatch(deleteDoctorExpDetail(deleteId));
      if (response?.meta?.requestStatus === "fulfilled") {
        dispatch(getDoctorExpByDoctorID(DoctorID));
        dispatch(closeDialog());
        setDeleteId(null);
      }
    }
  };

  const ActionCell = (props: any) => (
    <td
      className={props.className}
      style={{
        ...props.style,
        textAlign: "center",
        display: "flex",
        justifyContent: "space-evenly",
      }}
    >
      <IconButton
        type="button"
        fillMode="flat"
        size="small"
        title="Edit"
        onClick={() =>
          setEditItem({
            ...props.dataItem,
            FromMonthYear: props.dataItem.FromMonthYear
              ? moment(props.dataItem.FromMonthYear, "YYYY-MM-DD").toDate()
              : "",
            ToMonthYear: props.dataItem.ToMonthYear
              ? moment(props.dataItem.ToMonthYear, "YYYY-MM-DD").toDate()
              : "",
          })
        }
        icon={MdEdit}
        iconClassName="absolute-position"
        iconStyle={{ fontSize: "20px" }}
      />
      <IconButton
        type="button"
        fillMode="flat"
        size="small"
        title="Delete"
        className="color-red"
        onClick={() => {
          dispatch(openDialog("deleteDoctorExp"));
          setDeleteId(props.dataItem.ID);
        }}
        icon={MdDelete}
        iconClassName="absolute-position"
        iconStyle={{ fontSize: "24px" }}
      />
    </td>
  );

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 4 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <td {...(props as any).tdProps} colSpan={1} className={"table-cell"}>
      {props.children}
    </td>
  );

  return (
    <>
      {dialogName === "deleteDoctorExp" && (
        <AppDialog>
          <>
            <Typography.h5>Delete Experience</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this experience entry?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{ display: "flex", justifyContent: "end", gap: "10px" }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={handleDelete}
                  type="button"
                  disabled={loading}
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}

      <GridLayout style={{ marginTop: 20 }} gap={{ rows: 10, cols: 10 }}>
        <GridLayoutItem colSpan={4}>
          <ShadowCard style={{ padding: 20 }}>
            <Typography.h4 style={{ margin: "0 0 20px 0", color: "#495057" }}>
              {editItem ? "Edit Experience" : "Add Experience"}
            </Typography.h4>
            <Form
              key={formKey}
              onSubmit={editItem ? handleUpdate : handleAdd}
              initialValues={
                editItem || {
                  HospitalName: "",
                  Address: "",
                  City: "",
                  SpecialityID: "",
                  FromMonthYear: "",
                  ToMonthYear: "",
                }
              }
              render={(formRenderProps: FormRenderProps) => (
                <FormElement>
                  <GridLayout
                    style={{ marginRight: 60 }}
                    gap={{ rows: 15, cols: 20 }}
                    cols={[
                      { width: "25%" },
                      { width: "25%" },
                      { width: "25%" },
                      { width: "25%" },
                    ]}
                  >
                    <GridLayoutItem colSpan={2}>
                      <Field
                        name="HospitalName"
                        label="Hospital/Clinic Name"
                        placeholder="Hospital/Clinic Name"
                        component={FormTextField}
                        validator={requiredValidator}
                        astrike={true}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem colSpan={2}>
                      <Field
                        name="Address"
                        label="Full Address"
                        placeholder="Hospital Full Address"
                        component={FormTextField}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="SpecialityID"
                        label="Speciality Practiced"
                        placeholder="e.g., Cardiology"
                        isAddNew={true}
                        drawerName="specialityForm"
                        component={FormSelectionField}
                        options={Specialty?.map((item) => ({
                          value: item.ID,
                          label: item.Title,
                        }))}
                        validator={requiredValidator}
                        astrike={true}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="City"
                        label="City of hospital"
                        placeholder="City of hospital"
                        component={FormTextField}
                        validator={requiredValidator}
                        astrike={true}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="FromMonthYear"
                        label="Start date"
                        component={FormDatePicker}
                        validator={requiredValidator}
                        onChange={() =>
                          formRenderProps.onChange("ToMonthYear", {
                            value: "",
                          })
                        }
                        maxDate={moment().toDate()}
                        astrike={true}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="ToMonthYear"
                        label="End date"
                        component={FormDatePicker}
                        minDate={moment(
                          formRenderProps.valueGetter("FromMonthYear"),
                        ).toDate()}
                        // validator={requiredValidator}
                        // astrike={true}
                        maxDate={moment().toDate()}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem
                      colSpan={4}
                      style={{
                        display: "flex",
                        justifyContent: "end",
                        gap: "10px",
                      }}
                    >
                      {editItem && (
                        <Button
                          type="button"
                          fillMode={"outline"}
                          themeColor={"secondary"}
                          onClick={() => setEditItem(null)}
                        >
                          Cancel Edit
                        </Button>
                      )}
                      <ButtonWithLoading
                        label={editItem ? "Update" : "Add Experience"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || formLoading}
                        loading={formLoading}
                      />
                    </GridLayoutItem>
                  </GridLayout>
                </FormElement>
              )}
            />

            <Typography.h4 style={{ margin: "0 0 20px 0", color: "#495057" }}>
              Experiences ({expDetails?.length || 0})
            </Typography.h4>
            {expDetails && expDetails.length > 0 ? (
              <KendoGrid
                style={{ maxHeight: "calc(100vh - 400px)" }}
                data={expDetails.slice(page.skip, page.take + page.skip)}
                skip={page.skip}
                take={page.take}
                total={expDetails.length}
                pageable={{
                  buttonCount: 5,
                  pageSizes: [5, 10, "All"],
                  pageSizeValue,
                  type: "input",
                }}
                onPageChange={pageChange}
                cells={{ headerCell: HeaderCustomCell, data: MyDataCustomCell }}
              >
                <Column field="HospitalName" title="Hospital" />
                <Column field="SpecialityName" title="Speciality" />
                <Column field="City" title="City" />
                <Column
                  field="FromMonthYear"
                  title="From Date"
                  cells={{
                    data: (props: any) => (
                      <td>
                        {props.dataItem.FromMonthYear
                          ? moment(
                              props.dataItem.FromMonthYear,
                              "YYYY-MM-DD",
                            ).format("DD/MM/YYYY")
                          : ""}
                      </td>
                    ),
                  }}
                />
                <Column
                  field="ToMonthYear"
                  title="To Date"
                  cells={{
                    data: (props: any) => (
                      <td>
                        {props.dataItem.ToMonthYear
                          ? moment(
                              props.dataItem.ToMonthYear,
                              "YYYY-MM-DD",
                            ).format("DD/MM/YYYY")
                          : ""}
                      </td>
                    ),
                  }}
                />
                <Column
                  field="ID"
                  title="Actions"
                  width={110}
                  cells={{ data: ActionCell }}
                  filterable={false}
                  locked={true}
                />
              </KendoGrid>
            ) : (
              <AlertBox />
            )}
          </ShadowCard>
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

export default DoctorExpForm;
